import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@utils/cn';

/**
 * Textarea variants using class-variance-authority
 */
const textareaVariants = cva(
  'flex min-h-[80px] w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground disabled:border-muted transition-colors resize-none',
  {
    variants: {
      state: {
        default: 'border-input',
        error: 'border-destructive focus-visible:ring-destructive',
        success: 'border-success focus-visible:ring-success',
      },
      resize: {
        none: 'resize-none',
        vertical: 'resize-y',
        horizontal: 'resize-x',
        both: 'resize',
      },
    },
    defaultVariants: {
      state: 'default',
      resize: 'vertical',
    },
  }
);

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'>,
    VariantProps<typeof textareaVariants> {
  /**
   * Error message to display
   */
  error?: string;
  /**
   * Success message to display
   */
  success?: string;
  /**
   * Helper text to display below the textarea
   */
  helperText?: string;
  /**
   * Label for the textarea
   */
  label?: string;
  /**
   * Wrapper div className
   */
  wrapperClassName?: string;
  /**
   * Show character count
   */
  showCount?: boolean;
  /**
   * Maximum character count
   */
  maxLength?: number;
  /**
   * Auto-resize textarea based on content
   */
  autoResize?: boolean;
  /**
   * Minimum number of rows when auto-resize is enabled
   */
  minRows?: number;
  /**
   * Maximum number of rows when auto-resize is enabled
   */
  maxRows?: number;
  /**
   * Custom onChange handler
   */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>, value: string) => void;
  /**
   * Mark field as required. Displays asterisk next to label.
   */
  required?: boolean;
}

/**
 * Textarea Component
 *
 * A flexible textarea component with auto-resize, character count, and validation states.
 *
 * @example
 * // Basic textarea
 * <Textarea placeholder="Enter notes" />
 *
 * @example
 * // Textarea with label and character count
 * <Textarea
 *   label="Clinical Notes"
 *   showCount
 *   maxLength={500}
 * />
 *
 * @example
 * // Auto-resizing textarea
 * <Textarea
 *   label="Symptoms"
 *   autoResize
 *   minRows={3}
 *   maxRows={10}
 * />
 *
 * @example
 * // Textarea with error state
 * <Textarea
 *   label="Diagnosis"
 *   error="This field is required"
 * />
 *
 * @example
 * // With React Hook Form
 * const { register, formState: { errors } } = useForm();
 * <Textarea
 *   {...register('notes', { required: 'Notes are required' })}
 *   error={errors.notes?.message}
 *   label="Treatment Notes"
 * />
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      state,
      resize,
      error,
      success,
      helperText,
      label,
      wrapperClassName,
      showCount = false,
      maxLength,
      autoResize = false,
      minRows = 3,
      maxRows = 10,
      onChange,
      value,
      id,
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState((value as string) || '');
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

    // Generate a unique ID if not provided
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    // Determine state based on error/success
    const computedState = error ? 'error' : success ? 'success' : state || 'default';

    // Get current value (controlled or uncontrolled)
    const currentValue = value !== undefined ? String(value) : internalValue;
    const charCount = currentValue.length;

    // Auto-resize logic
    const adjustHeight = React.useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea || !autoResize) return;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';

      // Calculate line height
      const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight, 10);
      const minHeight = lineHeight * minRows;
      const maxHeight = lineHeight * maxRows;

      // Set height based on content, bounded by min and max
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }, [autoResize, minRows, maxRows]);

    // Adjust height on mount and when value changes
    React.useEffect(() => {
      adjustHeight();
    }, [currentValue, adjustHeight]);

    // Handle change
    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = event.target.value;

      // Update internal value if uncontrolled
      if (value === undefined) {
        setInternalValue(newValue);
      }

      // Call custom onChange
      onChange?.(event, newValue);

      // Adjust height if auto-resize is enabled
      if (autoResize) {
        adjustHeight();
      }
    };

    // Merge refs
    const mergedRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    return (
      <div className={cn('w-full', wrapperClassName)}>
        <div className="mb-2 flex items-center justify-between">
          {label && (
            <label
              htmlFor={inputId}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {label}
              {required && (
                <span className="ml-1 text-destructive" aria-label="required">
                  *
                </span>
              )}
            </label>
          )}
          {showCount && maxLength && (
            <span
              className={cn('text-xs text-muted-foreground', {
                'text-destructive': charCount > maxLength,
                'text-warning': charCount > maxLength * 0.9,
              })}
              aria-live="polite"
              aria-atomic="true"
            >
              {charCount} / {maxLength}
            </span>
          )}
        </div>
        <textarea
          className={cn(
            textareaVariants({ state: computedState, resize: autoResize ? 'none' : resize }),
            autoResize && 'overflow-hidden',
            className
          )}
          ref={mergedRef}
          id={inputId}
          value={value}
          maxLength={maxLength}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : helperText || success ? helperId : undefined}
          aria-required={required}
          onChange={handleChange}
          {...props}
        />
        {(error || success || helperText) && (
          <div
            id={error ? errorId : helperId}
            className={cn('mt-1.5 text-sm', {
              'text-destructive': error,
              'text-success': success,
              'text-muted-foreground': helperText && !error && !success,
            })}
            role={error ? 'alert' : 'status'}
            aria-live="polite"
          >
            {error || success || helperText}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea, textareaVariants };
